import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

import { Users, UserPlus, Search, Filter, RefreshCw, Building, Briefcase, User, Shield, LayoutGrid, List, UserCheck, UserX } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { USER_TYPES } from './assignationsShared';
import { AssignationsViews } from './AssignationsViews';
import { AssignationCreateModal } from './AssignationCreateModal';

const API = process.env.REACT_APP_BACKEND_URL;

export default function GestionAssignations({ getAuthHeader }) {
  const [assignments, setAssignments] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [users, setUsers] = useState({ members: [], coopers: [], experts: [] });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [viewMode, setViewMode] = useState('cards'); // 'cards', 'table', 'byAdmin'
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterAdmin, setFilterAdmin] = useState('all');
  const [_selectedAssignment, setSelectedAssignment] = useState(null);
  
  const [formData, setFormData] = useState({
    admin_id: '',
    assigned_user_id: '',
    assigned_user_type: 'member',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Charger les assignations
      const assignRes = await axios.get(
        `${API}/api/superadmin/assignments`,
        { headers: getAuthHeader() }
      );
      setAssignments(assignRes.data.assignments || []);
      
      // Charger les admins
      const adminsRes = await axios.get(
        `${API}/api/users?role=admin`,
        { headers: getAuthHeader() }
      );
      // Handle both old array format and new {users: [...]} format
      const adminsData = adminsRes.data.users || adminsRes.data || [];
      setAdmins(Array.isArray(adminsData) ? adminsData : []);
      
      // Charger les utilisateurs par type
      const [membersRes, coopersRes, expertsRes] = await Promise.all([
        axios.get(`${API}/api/members`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/coopers`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/experts`, { headers: getAuthHeader() }).catch(() => ({ data: [] }))
      ]);
      
      setUsers({
        members: membersRes.data || [],
        coopers: coopersRes.data?.coopers || coopersRes.data || [],
        experts: expertsRes.data?.experts || expertsRes.data || []
      });
      
    } catch {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAssignment = async () => {
    if (!formData.admin_id || !formData.assigned_user_id) {
      toast.error('Veuillez sélectionner un admin et un utilisateur');
      return;
    }

    try {
      await axios.post(
        `${API}/api/superadmin/assignments`,
        formData,
        { headers: getAuthHeader() }
      );
      
      toast.success('Assignation créée avec succès !');
      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleDeleteAssignment = async (id) => {
    if (!window.confirm('Supprimer cette assignation ?')) return;

    try {
      await axios.delete(
        `${API}/api/superadmin/assignments/${id}`,
        { headers: getAuthHeader() }
      );
      
      toast.success('Assignation supprimée');
      loadData();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setFormData({
      admin_id: '',
      assigned_user_id: '',
      assigned_user_type: 'member',
      notes: ''
    });
    setSelectedAssignment(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  // Statistiques
  const stats = useMemo(() => {
    const byType = {
      member: assignments.filter(a => a.assigned_user_type === 'member').length,
      cooper: assignments.filter(a => a.assigned_user_type === 'cooper').length,
      expert: assignments.filter(a => a.assigned_user_type === 'expert').length
    };
    
    const byAdmin = {};
    assignments.forEach(a => {
      const adminName = `${a.admin_prenom || ''} ${a.admin_nom || ''}`.trim();
      byAdmin[adminName] = (byAdmin[adminName] || 0) + 1;
    });
    
    return { total: assignments.length, byType, byAdmin };
  }, [assignments]);

  // Filtrage des assignations
  const filteredAssignments = useMemo(() => {
    return assignments.filter(a => {
      const matchesSearch = 
        (a.admin_nom || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (a.admin_prenom || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (a.assigned_user_nom || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (a.assigned_user_prenom || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesType = filterType === 'all' || a.assigned_user_type === filterType;
      const matchesAdmin = filterAdmin === 'all' || a.admin_id === filterAdmin;
      
      return matchesSearch && matchesType && matchesAdmin;
    });
  }, [assignments, searchTerm, filterType, filterAdmin]);

  // Grouper par admin pour la vue "Par Admin"
  const assignmentsByAdmin = useMemo(() => {
    const grouped = {};
    filteredAssignments.forEach(a => {
      const adminKey = a.admin_id;
      if (!grouped[adminKey]) {
        grouped[adminKey] = {
          admin: {
            id: a.admin_id,
            nom: a.admin_nom,
            prenom: a.admin_prenom,
            email: a.admin_email
          },
          assignments: []
        };
      }
      grouped[adminKey].assignments.push(a);
    });
    return Object.values(grouped);
  }, [filteredAssignments]);

  // Liste des utilisateurs selon le type sélectionné
  const availableUsers = useMemo(() => {
    switch (formData.assigned_user_type) {
      case 'member':
        return users.members.map(m => ({
          id: m.id,
          name: `${m.prenom || ''} ${m.nom || ''}`.trim() || m.email,
          email: m.email
        }));
      case 'cooper':
        return users.coopers.map(c => ({
          id: c.id,
          name: c.entreprise || c.raison_sociale || 
            (c.user ? `${c.user.prenom || ''} ${c.user.nom || ''}`.trim() : '') ||
            c.user?.email || 'Cooper',
          email: c.user?.email || c.email
        }));
      case 'expert':
        return users.experts.map(e => ({
          id: e.id,
          name: e.entreprise || `${e.prenom || ''} ${e.nom || ''}`.trim() || e.email,
          email: e.email
        }));
      default:
        return [];
    }
  }, [formData.assigned_user_type, users]);

  const getTypeConfig = (type) => USER_TYPES[type] || USER_TYPES.member;

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <UserCheck className="h-7 w-7 text-indigo-600" />
            Gestion des Assignations
          </h2>
          <p className="text-gray-500 mt-1">
            Assignez des utilisateurs aux administrateurs pour un suivi personnalisé
          </p>
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          {/* Recherche */}
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 w-56"
            />
          </div>
          
          {/* Filtre Type */}
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-40">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les types</SelectItem>
              <SelectItem value="member">Membres</SelectItem>
              <SelectItem value="cooper">COOPER&apos;S</SelectItem>
              <SelectItem value="expert">Experts</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Filtre Admin */}
          <Select value={filterAdmin} onValueChange={setFilterAdmin}>
            <SelectTrigger className="w-44">
              <Shield className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Admin" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les admins</SelectItem>
              {admins.map(admin => (
                <SelectItem key={admin.id} value={admin.id}>
                  {admin.prenom} {admin.nom}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Vue */}
          <div className="flex border rounded-lg overflow-hidden">
            <Button 
              variant={viewMode === 'cards' ? 'secondary' : 'ghost'} 
              size="sm"
              onClick={() => setViewMode('cards')}
              className="rounded-none"
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button 
              variant={viewMode === 'table' ? 'secondary' : 'ghost'} 
              size="sm"
              onClick={() => setViewMode('table')}
              className="rounded-none"
            >
              <List className="h-4 w-4" />
            </Button>
            <Button 
              variant={viewMode === 'byAdmin' ? 'secondary' : 'ghost'} 
              size="sm"
              onClick={() => setViewMode('byAdmin')}
              className="rounded-none"
            >
              <Users className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Rafraîchir */}
          <Button variant="outline" onClick={loadData} size="sm">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          
          {/* Nouvelle assignation */}
          <Button onClick={openCreateModal} className="bg-indigo-600 hover:bg-indigo-700">
            <UserPlus className="h-4 w-4 mr-1" />
            Nouvelle assignation
          </Button>
        </div>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-indigo-50 to-white border-indigo-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-bold text-indigo-700">{stats.total}</div>
                <div className="text-sm text-indigo-600">Assignations totales</div>
              </div>
              <UserCheck className="h-10 w-10 text-indigo-300" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-50 to-white border-green-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-bold text-green-700">{stats.byType.member}</div>
                <div className="text-sm text-green-600">Membres assignés</div>
              </div>
              <User className="h-10 w-10 text-green-300" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-bold text-purple-700">{stats.byType.cooper}</div>
                <div className="text-sm text-purple-600">COOPER&apos;S assignés</div>
              </div>
              <Building className="h-10 w-10 text-purple-300" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-sky-50 to-white border-sky-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-bold text-sky-700">{stats.byType.expert}</div>
                <div className="text-sm text-sky-600">Experts assignés</div>
              </div>
              <Briefcase className="h-10 w-10 text-sky-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Contenu principal */}
      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="h-10 w-10 animate-spin text-indigo-600" />
        </div>
      ) : filteredAssignments.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <UserX className="h-16 w-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-700">Aucune assignation</h3>
            <p className="text-gray-500 mt-1">
              {searchTerm || filterType !== 'all' || filterAdmin !== 'all' 
                ? 'Aucun résultat pour ces critères de recherche' 
                : 'Commencez par créer une nouvelle assignation'}
            </p>
            <Button onClick={openCreateModal} className="mt-4">
              <UserPlus className="h-4 w-4 mr-2" />
              Créer une assignation
            </Button>
          </CardContent>
        </Card>
      ) : (
        <AssignationsViews
          viewMode={viewMode}
          filteredAssignments={filteredAssignments}
          assignmentsByAdmin={assignmentsByAdmin}
          getTypeConfig={getTypeConfig}
          onDelete={handleDeleteAssignment}
        />
      )}

      {/* Modal Nouvelle Assignation */}
      <AssignationCreateModal
        open={showModal}
        onOpenChange={setShowModal}
        formData={formData}
        setFormData={setFormData}
        admins={admins}
        availableUsers={availableUsers}
        onCreate={handleCreateAssignment}
      />
    </div>
  );
}
